import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { random } from '@euler/utils';
import { range } from 'ramda';
import { memo } from 'react';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { Center } from './Center';
import { AdvancedImage } from './image/AdvancedImage';
import { AdvancedImageProps } from './image/AdvancedImage.shared';

const DefaultAvatarBgColors = [
  '#00AA55',
  '#009FD4',
  '#B381B3',
  '#939393',
  '#E3BC00',
  '#D47500',
  '#DC2A2A',
  '#d3155a',
  '#8e78ff',
  '#ff7300',
  '#fbb03b',
  '#ed1e78',
  '#009344',
  '#ed1c24',
  '#2e3191',
  '#fc7d7b',
  '#ffcc00',
  '#3aa17d',
  '#4f01bb',
  '#10c9bd',
  '#662d8c',
  '#06a7c4',
  '#0054ae',
];

export const Avatar = memo(
  ({
    name,
    textStyle,
    hideText,
    bold,

    // image props
    uri,
    source,
    placeholder,
    onLoad,
    blurhash,

    ...props
  }: {
    name: string;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    hideText?: boolean;
    // Render the initials in the bold face instead of the default light one
    // (use the Typography weight prop — a `fontFamily` in textStyle renders the
    // regular face on native).
    bold?: boolean;
  } & AdvancedImageProps) => {
    const { rpx } = useResponsiveContext();

    if (uri) {
      return (
        <AdvancedImage
          uri={uri}
          source={source}
          placeholder={placeholder}
          onLoad={onLoad}
          blurhash={blurhash}
          overflow="hidden"
          {...props}
        />
      );
    }

    if (!name) {
      const chars = range('a'.charCodeAt(0), 'z'.charCodeAt(0)).map(x =>
        String.fromCharCode(x),
      );
      name = random(chars);
    }

    const codepoint = name.codePointAt(0) ?? name.charCodeAt(0);
    const index = codepoint % DefaultAvatarBgColors.length;
    const bgColor = DefaultAvatarBgColors[index];

    return (
      <Center backgroundColor={bgColor} overflow="hidden" {...props}>
        {!hideText ? (
          <Typography.Text
            light={bold ? undefined : true}
            bold={bold ? true : undefined}
            size={rpx(10)}
            lineHeight={rpx(16)}
            color="#fff"
            textTransform="uppercase"
            style={textStyle}
          >
            {name.charAt(0)}
          </Typography.Text>
        ) : null}
      </Center>
    );
  },
);
